import { Button } from '@affine/component';
import {
  SettingHeader,
  SettingWrapper,
} from '@affine/component/setting-components';
import { WorkspaceServerService } from '@affine/core/modules/cloud';
import { WorkspacePermissionService } from '@affine/core/modules/permissions';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { WorkspaceFolderAccessPreset } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  assignDocumentToWorkspaceFolderMutation,
  type AssignDocumentInput,
  type AssignDocumentToWorkspaceFolderResponse,
  createWorkspaceFolderMutation,
  type CreateFolderInput,
  type CreateWorkspaceFolderResponse,
  type WorkspaceFolderDocumentCandidatesResponse,
  type UpdateFolderPolicyInput,
  type UpdateWorkspaceFolderPolicyResponse,
  updateWorkspaceFolderPolicyMutation,
  type WorkspaceFolder,
  type WorkspaceFolderDocumentCandidate,
  type WorkspaceFolderGql,
  type WorkspaceFoldersResponse,
  workspaceFolderDocumentCandidatesQuery,
  workspaceFoldersQuery,
} from './operations';
import * as styles from './styles.css';

const accessPresets = [
  WorkspaceFolderAccessPreset.Private,
  WorkspaceFolderAccessPreset.MemberRead,
  WorkspaceFolderAccessPreset.MemberWrite,
  WorkspaceFolderAccessPreset.AuthenticatedRead,
  WorkspaceFolderAccessPreset.PublicRead,
] as const;

type PendingOperation = 'create' | 'assign' | `policy:${string}` | null;
type LoadState = 'idle' | 'loading' | 'error';
type Feedback =
  | { kind: 'success'; message: string }
  | { kind: 'error'; message: string };

function updateFolder(
  folders: WorkspaceFolder[] | null,
  nextFolder: WorkspaceFolder
) {
  return folders?.map(folder =>
    folder.id === nextFolder.id ? nextFolder : folder
  );
}

function folderPath(folder: WorkspaceFolder) {
  return folder.path.join(' / ') || folder.name;
}

function accessPresetLabel(
  t: ReturnType<typeof useI18n>,
  accessPreset: WorkspaceFolderAccessPreset
) {
  const readOnly = t['com.affine.share-menu.option.link.readonly']();

  switch (accessPreset) {
    case WorkspaceFolderAccessPreset.Private:
      return t['com.affine.share-menu.share-privately']();
    case WorkspaceFolderAccessPreset.MemberRead:
      return `${t['com.affine.share-menu.option.permission.label']()} · ${t['com.affine.share-menu.option.permission.can-read']()}`;
    case WorkspaceFolderAccessPreset.MemberWrite:
      return `${t['com.affine.share-menu.option.permission.label']()} · ${t['com.affine.share-menu.option.permission.can-edit']()}`;
    case WorkspaceFolderAccessPreset.AuthenticatedRead:
      return `${t['com.affine.auth.toast.title.signed-in']()} · ${readOnly}`;
    case WorkspaceFolderAccessPreset.PublicRead:
      return `${t['com.affine.share-menu.option.link.label']()} · ${readOnly}`;
    default:
      return t['com.affine.share-menu.option.permission.no-access']();
  }
}

export const WorkspaceFolderSetting = () => {
  const t = useI18n();
  const workspace = useService(WorkspaceService).workspace;
  const workspaceServer = useService(WorkspaceServerService);
  const workspacePermission = useService(WorkspacePermissionService).permission;
  const server = useLiveData(workspaceServer.server$);
  const isOwnerOrAdmin = useLiveData(workspacePermission.isOwnerOrAdmin$);
  const [folders, setFolders] = useState<WorkspaceFolder[] | null>(null);
  const [listState, setListState] = useState<LoadState>('loading');
  const [documentCandidates, setDocumentCandidates] = useState<
    WorkspaceFolderDocumentCandidate[] | null
  >(null);
  const [candidateState, setCandidateState] = useState<LoadState>('loading');
  const [pendingOperation, setPendingOperation] =
    useState<PendingOperation>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [selectedDocumentId, setSelectedDocumentId] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState('');

  const isManagedWorkspace =
    workspace.flavour !== 'local' && !workspace.openOptions.isSharedMode;
  const canManageFolders = isManagedWorkspace && isOwnerOrAdmin === true;

  useEffect(() => {
    workspacePermission.revalidate();
  }, [workspacePermission]);

  const loadFolders = useCallback(async () => {
    if (!server) {
      return;
    }

    setListState('loading');
    setFeedback(null);
    try {
      const gql = server.gql as unknown as WorkspaceFolderGql;
      const data = await gql<WorkspaceFoldersResponse>({
        query: workspaceFoldersQuery,
        variables: { workspaceId: workspace.id },
      });
      setFolders(data.workspaceFolders);
      setListState('idle');
    } catch {
      setFolders(null);
      setListState('error');
    }
  }, [server, workspace.id]);

  const loadDocumentCandidates = useCallback(async () => {
    if (!server) {
      return;
    }

    setCandidateState('loading');
    try {
      const gql = server.gql as unknown as WorkspaceFolderGql;
      const data = await gql<WorkspaceFolderDocumentCandidatesResponse>({
        query: workspaceFolderDocumentCandidatesQuery,
        variables: { workspaceId: workspace.id },
      });
      setDocumentCandidates(data.workspaceFolderDocumentCandidates);
      setCandidateState('idle');
    } catch {
      setDocumentCandidates(null);
      setCandidateState('error');
    }
  }, [server, workspace.id]);

  useEffect(() => {
    if (!canManageFolders || !server) {
      return;
    }
    void loadFolders();
    void loadDocumentCandidates();
  }, [canManageFolders, loadDocumentCandidates, loadFolders, server]);

  const targetFolderId = useMemo(
    () => selectedFolderId || folders?.[0]?.id || '',
    [folders, selectedFolderId]
  );
  const targetDocumentId = useMemo(
    () => selectedDocumentId || documentCandidates?.[0]?.docId || '',
    [documentCandidates, selectedDocumentId]
  );

  const createFolder = useCallback(async () => {
    if (!server) {
      return;
    }

    setPendingOperation('create');
    setFeedback(null);
    try {
      const gql = server.gql as unknown as WorkspaceFolderGql;
      const input: CreateFolderInput = {
        workspaceId: workspace.id,
        parentFolderId: null,
        accessPreset: WorkspaceFolderAccessPreset.Private,
      };
      const data = await gql<CreateWorkspaceFolderResponse>({
        query: createWorkspaceFolderMutation,
        variables: { input },
      });
      setFolders(current => [...(current ?? []), data.createWorkspaceFolder]);
      setSelectedFolderId(data.createWorkspaceFolder.id);
      setFeedback({ kind: 'success', message: t['Created']() });
    } catch {
      setFeedback({
        kind: 'error',
        message: t['com.affine.share-menu.member-management.update-fail'](),
      });
    } finally {
      setPendingOperation(null);
    }
  }, [server, t, workspace.id]);

  const updatePolicy = useCallback(
    async (folderId: string, accessPreset: WorkspaceFolderAccessPreset) => {
      if (!server) {
        return;
      }

      setPendingOperation(`policy:${folderId}`);
      setFeedback(null);
      try {
        const gql = server.gql as unknown as WorkspaceFolderGql;
        const input: UpdateFolderPolicyInput = {
          workspaceId: workspace.id,
          folderId,
          accessPreset,
        };
        const data = await gql<UpdateWorkspaceFolderPolicyResponse>({
          query: updateWorkspaceFolderPolicyMutation,
          variables: { input },
        });
        setFolders(current =>
          updateFolder(current, data.updateWorkspaceFolderPolicy)
        );
        setFeedback({
          kind: 'success',
          message:
            t['com.affine.share-menu.member-management.update-success'](),
        });
      } catch {
        setFeedback({
          kind: 'error',
          message: t['com.affine.share-menu.member-management.update-fail'](),
        });
      } finally {
        setPendingOperation(null);
      }
    },
    [server, t, workspace.id]
  );

  const assignDocument = useCallback(async () => {
    if (!server || !targetDocumentId || !targetFolderId) {
      return;
    }

    setPendingOperation('assign');
    setFeedback(null);
    try {
      const gql = server.gql as unknown as WorkspaceFolderGql;
      const input: AssignDocumentInput = {
        workspaceId: workspace.id,
        docId: targetDocumentId,
        folderId: targetFolderId,
      };
      await gql<AssignDocumentToWorkspaceFolderResponse>({
        query: assignDocumentToWorkspaceFolderMutation,
        variables: { input },
      });
      setDocumentCandidates(current =>
        current?.filter(document => document.docId !== targetDocumentId)
      );
      setSelectedDocumentId(current =>
        current === targetDocumentId ? '' : current
      );
      setFeedback({
        kind: 'success',
        message: t['com.affine.share-menu.member-management.update-success'](),
      });
    } catch {
      setFeedback({
        kind: 'error',
        message: t['com.affine.share-menu.member-management.update-fail'](),
      });
    } finally {
      setPendingOperation(null);
    }
  }, [server, t, targetDocumentId, targetFolderId, workspace.id]);

  if (isOwnerOrAdmin === null) {
    return (
      <SettingHeader
        title={t['com.affine.m.selector.where-folder']()}
        subtitle={t['Loading']()}
      />
    );
  }

  if (!canManageFolders) {
    return (
      <>
        <SettingHeader title={t['com.affine.m.selector.where-folder']()} />
        <SettingWrapper>
          <div
            className={styles.blocked}
            data-testid="workspace-folder-forbidden"
          >
            {t['com.affine.no-permission']()}
          </div>
        </SettingWrapper>
      </>
    );
  }

  if (!server) {
    return (
      <>
        <SettingHeader title={t['com.affine.m.selector.where-folder']()} />
        <SettingWrapper>
          <div
            className={styles.blocked}
            role="alert"
            data-testid="workspace-folder-server-error"
          >
            {t['com.affine.share-menu.member-management.update-fail']()}
          </div>
        </SettingWrapper>
      </>
    );
  }

  return (
    <>
      <SettingHeader title={t['com.affine.m.selector.where-folder']()} />
      <SettingWrapper>
        <div className={styles.stack} data-testid="workspace-folder-panel">
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionTitle}>
                {t['com.affine.m.selector.where-folder']()}
              </div>
              <Button
                variant="primary"
                loading={pendingOperation === 'create'}
                disabled={listState !== 'idle' || pendingOperation !== null}
                onClick={() => void createFolder()}
                data-testid="workspace-folder-create"
              >
                {t['com.affine.rootAppSidebar.organize.add-folder']()}
              </Button>
            </div>

            {listState === 'loading' ? (
              <div
                className={styles.status}
                data-testid="workspace-folder-loading"
              >
                {t['Loading']()}
              </div>
            ) : null}

            {listState === 'error' ? (
              <div
                className={styles.errorRow}
                data-testid="workspace-folder-list-error"
              >
                <span className={styles.status}>
                  {t['com.affine.share-menu.member-management.update-fail']()}
                </span>
                <Button
                  variant="secondary"
                  onClick={() => void loadFolders()}
                  data-testid="workspace-folder-retry"
                >
                  {t['Retry']()}
                </Button>
              </div>
            ) : null}

            {listState === 'idle' && folders?.length === 0 ? (
              <div className={styles.empty}>
                {t['com.affine.rootAppSidebar.organize.empty']()}
              </div>
            ) : null}

            {listState === 'idle' && folders?.length ? (
              <div
                className={styles.folderList}
                data-testid="workspace-folder-list"
              >
                {folders.map(folder => (
                  <div className={styles.folderRow} key={folder.id}>
                    <div className={styles.folderIdentity}>
                      <div className={styles.folderName}>{folder.name}</div>
                      {folder.path.length > 1 ? (
                        <div className={styles.folderPath}>
                          {folderPath(folder)}
                        </div>
                      ) : null}
                    </div>
                    <label className={styles.policyField}>
                      <span className={styles.fieldLabel}>
                        {t['com.affine.share-menu.generalAccess']()}
                      </span>
                      <select
                        className={styles.select}
                        value={folder.accessPreset}
                        disabled={pendingOperation !== null}
                        onChange={event => {
                          void updatePolicy(
                            folder.id,
                            event.currentTarget
                              .value as WorkspaceFolderAccessPreset
                          );
                        }}
                        aria-label={folder.name}
                        data-testid="workspace-folder-policy"
                      >
                        {accessPresets.map(accessPreset => (
                          <option key={accessPreset} value={accessPreset}>
                            {accessPresetLabel(t, accessPreset)}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                ))}
              </div>
            ) : null}
          </section>

          {folders?.length ? (
            <section className={styles.section}>
              <div className={styles.assignmentForm}>
                <div className={styles.inputField}>
                  <span className={styles.fieldLabel}>{t['Document']()}</span>
                  {candidateState === 'loading' ? (
                    <div
                      className={styles.status}
                      data-testid="workspace-folder-document-loading"
                    >
                      {t['Loading']()}
                    </div>
                  ) : null}
                  {candidateState === 'error' ? (
                    <div
                      className={styles.errorRow}
                      data-testid="workspace-folder-document-error"
                    >
                      <span className={styles.status}>
                        {t[
                          'com.affine.share-menu.member-management.update-fail'
                        ]()}
                      </span>
                      <Button
                        variant="secondary"
                        onClick={() => void loadDocumentCandidates()}
                        disabled={pendingOperation !== null}
                        data-testid="workspace-folder-document-retry"
                      >
                        {t['Retry']()}
                      </Button>
                    </div>
                  ) : null}
                  {candidateState === 'idle' ? (
                    <select
                      className={styles.select}
                      value={targetDocumentId}
                      aria-label={t['Document']()}
                      disabled={
                        pendingOperation !== null || !documentCandidates?.length
                      }
                      onChange={event =>
                        setSelectedDocumentId(event.currentTarget.value)
                      }
                      data-testid="workspace-folder-document"
                    >
                      {documentCandidates?.length ? (
                        documentCandidates.map(document => (
                          <option key={document.docId} value={document.docId}>
                            {document.title} ({document.displayContext})
                          </option>
                        ))
                      ) : (
                        <option value="">
                          {t['com.affine.share-menu.invite-editor.no-found']()}
                        </option>
                      )}
                    </select>
                  ) : null}
                </div>
                <label>
                  <span className={styles.fieldLabel}>
                    {t['com.affine.m.selector.where-folder']()}
                  </span>
                  <select
                    className={styles.select}
                    value={targetFolderId}
                    onChange={event =>
                      setSelectedFolderId(event.currentTarget.value)
                    }
                    data-testid="workspace-folder-target"
                  >
                    {folders.map(folder => (
                      <option key={folder.id} value={folder.id}>
                        {folderPath(folder)}
                      </option>
                    ))}
                  </select>
                </label>
                <Button
                  variant="primary"
                  loading={pendingOperation === 'assign'}
                  disabled={
                    listState !== 'idle' ||
                    candidateState !== 'idle' ||
                    pendingOperation !== null ||
                    !targetDocumentId ||
                    !targetFolderId
                  }
                  onClick={() => void assignDocument()}
                  data-testid="workspace-folder-assign"
                >
                  {t['com.affine.rootAppSidebar.organize.folder.add-docs']()}
                </Button>
              </div>
            </section>
          ) : null}

          {feedback ? (
            <div
              className={
                feedback.kind === 'success'
                  ? `${styles.feedback} ${styles.feedbackSuccess}`
                  : `${styles.feedback} ${styles.feedbackError}`
              }
              role={feedback.kind === 'error' ? 'alert' : 'status'}
              data-testid="workspace-folder-feedback"
            >
              {feedback.message}
            </div>
          ) : null}
        </div>
      </SettingWrapper>
    </>
  );
};
